import { ContentRepository } from "../content/repository";
import type {
  BossActionDocument,
  DialogueId,
  EncounterId,
  GeneratorId,
  LevelDocument,
  LevelId,
  WeaponId,
  ProjectileDocument,
} from "../content/types";
import { Key, MovieClip, Sprite } from "../runtime";
import {
  GuiAction,
  PlayerAction,
  PlayerDeathAction,
  PlayerGuiAction,
  PlayerSound,
} from "./actions";
import { whiteBlock } from "./assets";
import { BlackHole } from "./BlackHole";
import { BgGenerator } from "./BgGenerator";
import { Camera, CameraTarget } from "./Camera";
import type { Viewport } from "./Camera";
import { Cleanser } from "./Cleanser";
import type { EnemyHitData } from "./combat";
import { NO_INPUT } from "./data/input";
import type { SceneName } from "./data/scenes";
import { SWORD_COLOURS } from "./data/swordColours";
import { WEAPONS } from "./data/weapons";
import { Effect } from "./Effect";
import { Entity } from "./Entity";
import { Hud } from "./Hud";
import type { DialogueResolver } from "./events";
import { EntityFactory } from "./entities/EntityFactory";
import { EffectId, EntityId } from "./ids";
import { Knight } from "./Knight";
import type { Barrier, Level, Obstacle, RuntimeEncounter } from "./level";
import { buildLevel } from "./level";
import { MessageBox } from "./MessageBox";
import { ObjectGenerator } from "./ObjectGenerator";
import { PauseScreen } from "./PauseScreen";
import { Rain } from "./Rain";
import type { ItemSlot } from "./SaveFile";
import { ScorePop } from "./ScorePop";
import { SoundFX } from "./SoundFX";
import { Strike } from "./Strike";
import { Thrown } from "./Thrown";

/**
 * Mutable state shared with the scene/persistence orchestrator for one level
 * run. Arrays and records intentionally retain the SaveState object identities.
 */
export interface LevelSession {
  levelId: LevelId;
  selectedWeapon: number;
  unlockedLevels: LevelId[];
  highScores: Record<LevelId, number>;
  itemData: Record<LevelId, ItemSlot>;
  itemFound: { weaponChip: number; armourChip: number };
  weaponData: number[];
  savedProgress: number;
  hyperArmour: boolean;
  eventStack: DialogueId[];
  /** God mode: no damage, pinned energy, and free, uncapped special attacks. */
  cheats: boolean;
}

export interface LevelRunnerCallbacks {
  save(): void;
  completeLevel(levelId: LevelId, score: number): void;
  requestScene(scene: SceneName, colored?: boolean, slowFade?: boolean): void;
}

/**
 * Sounds that several systems can request in one frame. Requesting sets the
 * slot to 10; the drain at the end of the frame plays it once and counts the
 * slot back down, which is how the original throttled repeats.
 */
const SOUND_SLOTS: readonly {
  readonly cooldown: number;
  readonly play: (fx: SoundFX) => void;
  readonly flash?: boolean;
}[] = [
  { cooldown: 3, play: (fx) => fx.explodeHuge(), flash: true },
  { cooldown: 2, play: (fx) => fx.hitEnemy() },
  { cooldown: 3, play: (fx) => fx.explodeEnemy() },
  { cooldown: 3, play: (fx) => fx.hitEnemySpecial() },
  { cooldown: 3, play: (fx) => fx.explodeBig(), flash: true },
  { cooldown: 3, play: (fx) => fx.missileLaunch() },
];

const enum Slot {
  ExplodeHuge = 0,
  Hit = 1,
  ExplodeEnemy = 2,
  HitSpecial = 3,
  ExplodeBig = 4,
  Missile = 5,
}

const LEVEL_MUSIC_TRACKS: Readonly<Record<string, number>> = {
  level01: 1,
  level02: 2,
  level03: 3,
  level04: 4,
  level05: 5,
  level06: 6,
  bossIntro: 9,
};

const WEAPON_SLOTS: Readonly<Record<WeaponId, number>> = {
  "energy-crusher": 2,
  "gravity-slicer": 3,
};

const COMPLETION_SCENES: Readonly<
  Record<LevelDocument["completion"]["scene"], SceneName>
> = {
  "level-select": "level select",
  ending: "ending",
  title: "title",
};

const PROJECTILE_EFFECTS: Readonly<Record<string, EntityId>> = {
  "orb-shot": EntityId.OrbShot,
  "large-sword-charge": EntityId.LargeSwordCharge,
  "rotating-laser": EntityId.RotatingLaser,
  "sickle-shot": EntityId.SickleShot,
  missile: EntityId.Missile,
  "laser-shot": EntityId.LaserShot,
};

const PROJECTILE_SOUND_PLAYERS: Readonly<
  Record<string, (soundFX: SoundFX) => void>
> = {
  enemyShotOrb: (soundFX) => soundFX.enemyShotOrb(),
  enemyShotSickle: (soundFX) => soundFX.enemyShotSickle(),
  enemyShotLaser: (soundFX) => soundFX.enemyShotLaser(),
  missileLaunch: (soundFX) => soundFX.missileLaunch(),
};

const RELEASED = null as unknown as never;
const EMPTY_OBSTACLES: never[] = [];

interface Projectile {
  readonly effect: EntityId;
  readonly angle: number;
  readonly absolute?: boolean;
}

interface CompiledRangedPattern {
  readonly projectiles: readonly Projectile[];
  readonly sound?: string;
  readonly finalRepeatProjectile?: Projectile;
  readonly finalRepeatSound?: string;
}

interface RuntimeShotChannel {
  readonly id: string;
  counter: number;
  readonly limit: number;
  repeat: number;
  readonly repeatLimit: number;
  readonly repeatSpacing: number;
  alternate: boolean;
}

interface CompiledBossPattern {
  readonly projectiles: readonly Projectile[];
  readonly spin?: number;
  readonly sound?: string;
  readonly missileSound?: boolean;
}

interface BossState {
  actionCounter: number;
  actionLimit: number;
  actionCurrent: number;
  shotCounter: number;
  burstsFired: number;
  shotPattern: string | null;
  shotLimit: number;
  shotSpacing: number;
  shooting: boolean;
  shotX: number;
  shotY: number;
  dead: boolean;
  explodeCounter: number;
  shotAngle: number;
  explodeDist: number;
  actions: ReadonlyMap<number, BossActionDocument>;
  patterns: Readonly<Record<string, CompiledBossPattern>>;
}

export class LevelRunner extends MovieClip {
  private objectHolder!: MovieClip;
  private gui!: Hud;
  private knight!: Knight;
  private mBox!: MessageBox;
  private mBoxArray: string[] = [];
  private bg!: BgGenerator;
  /** Enemies and bosses. */
  private og!: ObjectGenerator;
  /** Projectiles and one-shot effects. */
  private eg!: ObjectGenerator;
  /** Blades in flight; vanilla allows one, cheats allow as many as are thrown. */
  private readonly thrown: Thrown[] = [];
  private rain!: Rain;
  private effects: Effect[] = [];
  private strikes: Strike[] = [];
  private scoreArray: ScorePop[] = [];
  private readonly barrierObstaclePool: Obstacle[] = [];
  private readonly barrierObstacleArray: Obstacle[] = [];
  private triggerCurrent: EncounterId | null = null;
  private currentEncounter: RuntimeEncounter | null = null;
  private triggerFinished = false;
  private pauseScreen!: PauseScreen;
  private paused = false;
  private pausePressed = false;
  private readonly view: Viewport = {
    frameX: 720,
    frameY: 480,
    frameXHalf: 360,
    frameYHalf: 240,
    levelX: 2000,
    levelY: 2000,
  };
  private score = 0;
  private soundArray = [0, 0, 0, 0, 0, 0, 0, 0];
  private input = NO_INPUT.slice();
  private flasher!: MovieClip;
  private flashAlpha = 0;
  private flashTotal = 0;
  private survivalWave = 0;
  private generatorsTotal = 0;
  private readonly generatorMiniMap = new Map<GeneratorId, number>();
  private cleanser!: Cleanser;
  private shotChannels: RuntimeShotChannel[] = [];
  private readonly shotChannelsById = new Map<string, RuntimeShotChannel>();
  private readonly camera = new Camera();
  private gotoScene: SceneName = "level select";
  private frozen = false;
  private blackHole!: BlackHole;
  private blackHoleTick = 0;
  private specialDamage = 0;
  private cleanserStage = 0;
  private hasEnemies = false;
  private bossData!: BossState;
  private readonly levelDocument: LevelDocument;
  private readonly compiledLevel: Level;
  private readonly entityFactory: EntityFactory;
  private readonly rangedPatterns: ReadonlyMap<string, CompiledRangedPattern>;
  private readonly resolveDialogue: DialogueResolver;
  private readonly bossWeaponSlot: number | undefined;

  constructor(
    private readonly content: ContentRepository,
    private readonly session: LevelSession,
    private readonly soundFX: SoundFX,
    private readonly callbacks: LevelRunnerCallbacks,
  ) {
    super();
    this.levelDocument = content.level(session.levelId);
    this.compiledLevel = buildLevel(this.levelDocument, content.tileCatalog);
    this.entityFactory = new EntityFactory(content);
    const weaponReward = this.levelDocument.boss?.weaponReward;
    this.bossWeaponSlot =
      weaponReward === undefined ? undefined : WEAPON_SLOTS[weaponReward];
    if (weaponReward !== undefined && this.bossWeaponSlot === undefined) {
      throw new Error(`unknown boss weapon reward '${weaponReward}'`);
    }
    this.rangedPatterns = compileRangedPatterns(content, this.levelDocument);
    this.resolveDialogue = (id) => content.dialogue(this.levelDocument.id, id);
    this.resetShots(0);
    this.setupLevelScene();
  }

  get selectedWeapon(): number {
    return this.knight.weapon;
  }
  /** Maps a stage-space UI press to its game input slot. */
  pointerInputAt(stageX: number, stageY: number): number | null {
    if (this.paused) return null;
    const weapon = this.gui.weaponAt(stageX, stageY);
    if (weapon != null) return weapon + 5;
    return this.gui.eventObject != null ? 4 : null;
  }

  private get levelId(): LevelId {
    return this.levelDocument.id;
  }

  private get savedWeapon(): number {
    return this.session.selectedWeapon;
  }

  private set savedWeapon(value: number) {
    this.session.selectedWeapon = value;
  }

  private get highScores(): Record<LevelId, number> {
    return this.session.highScores;
  }

  private get itemData(): Record<LevelId, ItemSlot> {
    return this.session.itemData;
  }

  private get itemFound(): { weaponChip: number; armourChip: number } {
    return this.session.itemFound;
  }

  private get weaponData(): number[] {
    return this.session.weaponData;
  }
  private get savedProgress(): number {
    return this.session.savedProgress;
  }

  private set savedProgress(value: number) {
    this.session.savedProgress = value;
  }

  private get hyperArmour(): boolean {
    return this.session.hyperArmour;
  }

  private set hyperArmour(value: boolean) {
    this.session.hyperArmour = value;
  }

  private get eventStack(): DialogueId[] {
    return this.session.eventStack;
  }

  private setupLevelScene(): void {
    this.score = 0;
    const survival = this.compiledLevel.survival !== null;
    if (survival && this.savedWeapon === 4) {
      this.savedWeapon = 0;
    }
    const weapons = this.session.cheats
      ? [2, 2, 2, 2, 2]
      : survival
        ? this.levelDocument.survival!.weaponPreset.slice()
        : this.weaponData;
    const displayLevel = this.content.hasNode(this.levelId)
      ? this.content.node(this.levelId).label
      : this.levelDocument.kind === "tutorial"
        ? "TRAINING"
        : "SURVIVAL";
    this.gui = new Hud(
      "level",
      displayLevel,
      weapons,
      this.hyperArmour,
      this.resolveDialogue,
    );
    this.bg = new BgGenerator(
      this.view.frameX,
      this.view.frameY,
      this.compiledLevel,
    );
    this.addChild(this.bg);
    this.flasherEvents(0);
    this.blackHole = new BlackHole();
    this.addChild(this.blackHole);
    this.view.levelX = this.bg.levelData.bgX * 512;
    this.view.levelY = this.bg.levelData.bgY * 512;
    this.og = new ObjectGenerator(
      0,
      this.view.frameX,
      this.view.frameY,
      this.bg.levelData.bgX,
      this.bg.levelData.bgY,
      this.entityFactory,
    );
    this.addChild(this.og);
    this.og.barriers = this.bg.barriers;
    this.objectHolder = new MovieClip();
    this.addChild(this.objectHolder);
    // pre-warms the six sword-trail effects off screen
    const trailSwordLevel = this.hyperArmour ? -1 : 0;
    for (let trailIndex = 0; trailIndex < 6; trailIndex++) {
      this.specialEffect(
        EffectId.SwordTrail,
        -100,
        -100,
        trailIndex,
        trailSwordLevel,
      );
    }
    this.knight = new Knight(
      this.view.levelX,
      this.view.levelY,
      this.bg.startPoint.x,
      this.bg.startPoint.y,
      this.bg.triggerPass,
      this.savedWeapon,
      this.levelDocument.kind !== "tutorial",
      weapons,
      this.hyperArmour,
    );
    this.knight.cheats = this.session.cheats;
    this.camera.moveTo(this.knight.x, this.knight.y);
    this.objectHolder.addChild(this.knight);
    this.eg = new ObjectGenerator(1, this.view.frameX, this.view.frameY);
    this.addChild(this.eg);
    if (this.bg.raining > 0) {
      this.rain = new Rain();
      this.addChild(this.rain);
    }
    this.addChild(this.gui);
    this.gui.updateWeapon(this.knight.weapon);
    this.gui.setupMiniMap(this.bg.levelData.bgX, this.bg.levelData.bgY);
    if (this.bg.bossData != null) {
      this.og.generateBoss(
        this.bg.bossData.xx,
        this.bg.bossData.yy,
        this.bg.bossData.entity,
        this.bg.bossData.variant,
      );
      this.bossInit();
    }
    for (const generator of this.compiledLevel.generators) {
      ++this.generatorsTotal;
      ++this.gui.generatorsTotal;
      this.og.generateEnemies(
        generator.xx,
        generator.yy,
        "generator",
        1,
        generator.id,
      );
      this.generatorMiniMap.set(generator.id, this.gui.mmItem.length);
      this.gui.addMiniMapItem(1, generator.xx, generator.yy);
    }
    this.cleanserSetup();
    const itemState = this.itemData[this.levelId];
    for (const item of this.bg.itemPickup) {
      if (item.kind === "large-xenos") {
        this.specialEffect(EffectId.LargeXenosPickup, item.xx, item.yy);
      } else if (itemState.found === 0) {
        this.specialEffect(
          item.kind === "weapon-chip"
            ? EffectId.CircuitPickup
            : EffectId.Circuit2Pickup,
          item.xx,
          item.yy,
        );
      }
    }
    if (this.levelDocument.kind === "tutorial" || this.bg.bossData !== null) {
      this.gui.enemyAttacksOn = false;
    }
    const musicTrack = LEVEL_MUSIC_TRACKS[this.compiledLevel.music];
    if (musicTrack === undefined) {
      throw new Error(`unknown level music '${this.compiledLevel.music}'`);
    }
    this.soundFX.musicStart(musicTrack);
  }

  /** Advances every active-level system once in the original frame order. */
  update(input: boolean[]): void {
    this.input = input;
    if (!this.paused) {
      if (this.eg.freeze > 0) {
        --this.eg.freeze;
      } else if (this.frozen) {
        this.guiEvents();
      } else if (this.knight.freeze === 0) {
        this.levelEvents();
      } else {
        this.guiEvents();
        this.playerEvents(this.barrierObstacles());
      }
      if (this.knight.powerXOn) {
        this.knight.maxPowerX(1);
      }
      this.messageBoxEvents();
      this.scoreEvents();
    }
    this.pauseEvents();
    this.flasherEvents(2);
  }

  /** Captures the final weapon selection before Game removes this subtree. */
  teardown(): void {
    this.session.selectedWeapon = this.knight.weapon;
  }

  private saveData(): void {
    this.callbacks.save();
  }

  private startFade(_fadeOut = false, colored = false, slowFade = false): void {
    this.callbacks.requestScene(this.gotoScene, colored, slowFade);
  }

  private pauseEvents(): void {
    if (Key.isDown(80)) {
      if (!this.pausePressed) {
        this.pausePressed = true;
        this.paused = !this.paused;
        if (this.paused) {
          this.pauseScreen = new PauseScreen();
          this.addChild(this.pauseScreen);
        } else {
          this.removeChild(this.pauseScreen);
          this.pauseScreen = RELEASED;
        }
      }
    } else if (this.pausePressed) {
      this.pausePressed = false;
    }
    if (!this.paused || !Key.isDown(27) || !this.pauseScreen.pressEscape())
      return;
    if (
      this.compiledLevel.survival === null &&
      this.itemData[this.levelId].found === 1
    ) {
      this.itemData[this.levelId].found = 0;
    }
    if (this.cleanserStage > 0) {
      this.soundFX.cleanserLoop(1);
    }
    this.soundFX.musicFadeOut(0.05);
    if (this.compiledLevel.survival === null) {
      this.gotoScene = "level select";
      if (this.savedProgress < 2) {
        this.savedProgress = 2;
        this.saveData();
      }
    } else {
      this.gotoScene = "main menu";
    }
    this.startFade(true);
  }

  // ------------------------------------------------------------- level tick
  private levelEvents(): void {
    this.scrolling();
    const previousSwordLevel = this.knight.swordLevel | 0;
    const previousHp = this.knight.hpCurrent | 0;
    const barrierArray = this.barrierObstacles();
    this.playerEvents(barrierArray);
    const enemyData = this.og.update(
      this.knight.x,
      this.knight.y,
      this.objectHolder.x,
      this.objectHolder.y,
      this.bg.obsArray,
      barrierArray,
      this.gui.enemyAttacksOn,
    );
    const emptyArray = EMPTY_OBSTACLES;
    this.eg.update(
      this.knight.x,
      this.knight.y,
      this.objectHolder.x,
      this.objectHolder.y,
      emptyArray,
      emptyArray,
      false,
    );
    this.bg.update(this.objectHolder.x, this.objectHolder.y);
    this.meleeEvents(enemyData);
    this.dangerZoneEvents();
    this.generatorEvents();
    this.drainSmoke();
    this.drainSpawnerQueues();
    if (this.gui.enemyAttacksOn) {
      this.advanceEnemyFire();
    }
    if (this.compiledLevel.bossData !== null) {
      this.bossUpdate();
    }
    if (this.rain != null) {
      this.rain.update(this.objectHolder.x, this.objectHolder.y);
    }
    this.shotsVsPlayer();
    this.blackHoleEvents();
    this.enemyMelee();
    this.effectCycle();
    this.strikeCycle();
    this.guiEvents();
    if (this.cleanser != null) {
      this.cleanserEvents();
    }
    this.drainSoundSlots();
    if (this.knight.hpCurrent !== previousHp) {
      if (this.knight.hpCurrent < previousHp) {
        this.soundFX.damage();
        this.gui.damage();
      }
      this.gui.updateHP(this.knight.hpCurrent);
    }
    this.gui.updateXenos(this.knight.energy, this.knight.swordLevel);
    if (
      previousSwordLevel !== this.knight.swordLevel &&
      this.knight.swordLevel === 3
    ) {
      this.flasherEvents(1, 1);
      this.soundFX.shine();
      this.eg.freeze = 40;
      this.mBoxArray.push("MAX POWER!");
    }
  }

  /** Resolves the sword and every blade in flight against what they overlap. */
  private meleeEvents(enemyData: readonly EnemyHitData[]): void {
    for (const thrown of this.thrown) {
      if (thrown.hitCD === 0) this.thrownMelee(thrown, enemyData);
    }
    // a blade in flight is the knight's only weapon in vanilla, so the sword
    // clauses below cannot fire at the same time unless cheats keep it in hand
    const swinging =
      (this.knight.animState === 0 &&
        this.knight.hasWeapon &&
        this.knight.moving) ||
      (this.knight.spinOn && this.knight.hitCD === 0) ||
      (this.knight.animState === 4 && this.knight.hitCD === 0);
    if (!swinging) return;
    const weaponDamage = Math.floor(
      (this.knight.swordLevel * 0.5 + 1) * WEAPONS[this.knight.weapon].damage,
    );
    let hit = 0;
    let totalHit = 0;
    for (let enemyIndex = 0; enemyIndex < enemyData.length; enemyIndex++) {
      if (
        totalHit >= this.knight.swordLevel + 3 ||
        this.og.allObjects[enemyIndex].isMine
      )
        continue;
      const enemyReturn = this.knight.slashHit(enemyData[enemyIndex]) | 0;
      if (enemyReturn <= 0) continue;
      if (!this.knight.spinOn && this.knight.animState !== 4) {
        this.og.hitEnemy(
          enemyIndex,
          totalHit,
          this.knight.targetAngle,
          weaponDamage,
        );
      } else {
        this.og.hitEnemy(
          enemyIndex,
          totalHit,
          this.knight.targetAngle,
          this.specialDamage,
        );
        this.knight.hitCD = 12;
      }
      hit = enemyReturn;
      this.knight.updateEnergy();
      totalHit++;
    }
    if (
      hit > 0 &&
      this.knight.hasWeapon &&
      this.knight.animState !== 2 &&
      this.knight.animState !== 4
    ) {
      this.knight.changeAnimState(1);
    }
  }

  private thrownMelee(
    thrown: Thrown,
    enemyData: readonly EnemyHitData[],
  ): void {
    let totalHit = 0;
    for (let enemyIndex = 0; enemyIndex < enemyData.length; enemyIndex++) {
      if (
        totalHit >= this.knight.swordLevel + 3 ||
        this.og.allObjects[enemyIndex].isMine
      )
        continue;
      const enemyReturn = thrown.slashHit(enemyData[enemyIndex]) | 0;
      this.shotsVsThrown(thrown);
      if (enemyReturn <= 0) continue;
      this.og.hitEnemy(
        enemyIndex,
        totalHit,
        thrown.targetAngle,
        this.specialDamage,
      );
      this.knight.updateEnergy();
      totalHit++;
    }
  }

  private generatorEvents(): void {
    if (this.og.generatorDamaged.length === 0) return;
    const damaged = this.og.generatorDamaged[0];
    const marker = this.generatorMiniMap.get(damaged.id);
    if (marker === undefined) {
      throw new Error(`unknown damaged generator '${damaged.id}'`);
    }
    this.gui.mmItem[marker].id = 10;
    this.bg.createGeneratorDamaged(damaged.xx, damaged.yy);
    this.og.generatorDamaged.splice(0, 1);
    --this.generatorsTotal;
    this.flashTotal = 2;
    if (this.generatorsTotal === 0) {
      this.win();
    }
  }

  private drainSmoke(): void {
    for (const smoke of this.bg.smokeArray) {
      this.eg.generateEffect(EntityId.Smoke, smoke.xx, smoke.yy, smoke.a);
    }
    this.bg.smokeArray.length = 0;
  }

  /** Empties the one-shot queues the object generators fill during their update. */
  private drainSpawnerQueues(): void {
    if (this.og.soundArray.length > 0) {
      const soundID = this.og.soundArray[0] | 0;
      if (soundID === 0) this.soundFX.mine1();
      if (soundID === 1) this.soundFX.mine2();
      if (soundID === 2) this.soundFX.effect3();
      this.og.soundArray.splice(0, 1);
    }
    if (this.eg.soundArray.length > 0) {
      // reads og's queue, not eg's -- an original quirk kept as is
      if ((this.og.soundArray[0] | 0) === 0) {
        this.requestSound(Slot.ExplodeBig);
        this.camera.shake(20);
      }
      this.eg.soundArray.splice(0, 1);
    }
    if (this.og.rewardArray.length > 0) {
      const reward = this.og.rewardArray[0];
      this.scorePopUp(reward.xx, reward.yy, reward.points, true);
      this.og.rewardArray.splice(0, 1);
    }
    if (this.og.itemDropArray.length > 0) {
      const drop = this.og.itemDropArray[0];
      this.specialEffect(drop.id, drop.xx, drop.yy);
      this.og.itemDropArray.splice(0, 1);
    }
    if (this.og.effectArray.length > 0) {
      const effect = this.og.effectArray[0];
      this.specialEffect(effect.id, effect.xx, effect.yy);
      this.og.effectArray.splice(0, 1);
    }
    for (const explode of this.og.explodeArray) {
      this.explodeSound((explode.id | 0) as EntityId);
      this.eg.generateEffect(explode.id, explode.xx, explode.yy, explode.extra);
    }
    this.og.explodeArray.length = 0;
    for (const strike of this.og.strikeArray) {
      const strikeMC = new Strike(this.knight.swordLevel, strike.xx, strike.yy);
      this.objectHolder.addChild(strikeMC);
      this.strikes.push(strikeMC);
    }
    this.og.strikeArray.length = 0;
  }

  private explodeSound(id: EntityId): void {
    switch (id) {
      case EntityId.Spark:
        this.requestSound(
          this.knight.animState === 1 ? Slot.Hit : Slot.HitSpecial,
        );
        break;
      case EntityId.Explosion:
        this.requestSound(Slot.ExplodeEnemy);
        break;
      case EntityId.LargeExplosion:
        this.requestSound(Slot.ExplodeBig);
        this.camera.shake(32);
        break;
      case EntityId.HugeExplosion:
        this.requestSound(
          Math.ceil(Math.random() * 2) === 1
            ? Slot.ExplodeHuge
            : Slot.ExplodeBig,
        );
        this.camera.shake(39);
        break;
    }
  }

  /** Ticks authored enemy weapon channels in their declared order. */
  private advanceEnemyFire(): void {
    for (const channel of this.shotChannels) {
      ++channel.counter;
      if (channel.counter !== channel.limit) continue;
      this.enemyFire(channel.id);
      if (channel.repeat >= channel.repeatLimit) {
        channel.repeat = 0;
        channel.counter = 0;
        channel.alternate = !channel.alternate;
      } else {
        ++channel.repeat;
        channel.counter -= channel.repeatSpacing;
      }
    }
  }

  private drainSoundSlots(): void {
    SOUND_SLOTS.forEach((slot, i) => {
      if (this.soundArray[i] !== 10) return;
      this.soundArray[i] = slot.cooldown;
      slot.play(this.soundFX);
      if (slot.flash) this.flasherEvents(1, 1);
    });
    for (let i = 0; i < this.soundArray.length; i++) {
      if (this.soundArray[i] > 0) --this.soundArray[i];
    }
  }

  /** Queues a throttled sound; ignored while the slot is still counting down. */
  private requestSound(slot: Slot): void {
    if (this.soundArray[slot] === 0) {
      this.soundArray[slot] = 10;
    }
  }

  private win(): void {
    if (this.levelDocument.kind === "tutorial") {
      this.savedProgress = 2;
      this.saveData();
    } else if (this.levelDocument.kind !== "survival") {
      this.callbacks.completeLevel(this.levelId, this.score);
    }
    if (this.cleanserStage > 0) {
      this.soundFX.cleanserLoop(1);
    }
    this.gui.setupMissionBar(1);
    if (this.bg.bossData === null) {
      this.soundFX.musicFadeOut(0.01);
    }
  }

  /** Timed area-of-effect blasts queued by mines and boss slams. */
  private dangerZoneEvents(): void {
    for (
      let dangerZoneIndex = 0;
      dangerZoneIndex < this.og.dangerZone.length;
      dangerZoneIndex++
    ) {
      const zone = this.og.dangerZone[dangerZoneIndex];
      ++zone.frameCounter;
      if (zone.frameCounter !== zone.frameActive) continue;
      this.knight.shotCollision(zone.xx, zone.yy, zone.hitRadius, zone.damage);
      for (
        let enemyIndex = 0;
        enemyIndex < this.og.allObjects.length;
        enemyIndex++
      ) {
        const enemy = this.og.allObjects[enemyIndex];
        if (enemy.dead || enemy.isMine) continue;
        const dx = zone.xx - enemy.xPos;
        const dy = zone.yy - enemy.yPos;
        if (Math.sqrt(dx * dx + dy * dy) < zone.hitRadius) {
          this.og.hitEnemy(enemyIndex, 1, this.knight.targetAngle, 85);
        }
      }
      this.og.dangerZone.splice(dangerZoneIndex, 1);
      dangerZoneIndex--;
    }
  }

  // ------------------------------------------------------------- player

  private playerEvents(bArray: Obstacle[]): void {
    if (this.knight.dead) {
      this.playerDeathEvents();
      return;
    }
    // dialogue takes the controls away and stops the xenos bar draining
    const busy = this.gui.eventObject != null;
    const playerInput = busy || !this.knight.control ? NO_INPUT : this.input;
    const knightAction = this.knight.update(
      playerInput,
      this.bg.obsArray,
      bArray,
      !busy,
    );
    this.gui.mmItem[0].xx = this.knight.x;
    this.gui.mmItem[0].yy = this.knight.y;
    this.triggerEvents();
    this.camera.xOffTarget = knightAction.xAxis * -85;
    this.camera.yOffTarget = knightAction.yAxis * -85;
    this.playerSpecial((knightAction.action | 0) as PlayerAction);
    this.playerSound((knightAction.sound | 0) as PlayerSound);
    if (knightAction.guiAction === PlayerGuiAction.WeaponChanged) {
      this.gui.updateWeapon(this.knight.weapon);
    }
    if (knightAction.action === PlayerAction.ThrowWeapon) {
      const [angle, speed, spin, level] = this.knight.thrownReturn();
      const blade = new Thrown(
        this.knight.x,
        this.knight.y,
        angle,
        speed,
        spin,
        level,
      );
      this.objectHolder.addChild(blade);
      this.thrown.push(blade);
    }
    for (let index = this.thrown.length - 1; index >= 0; index--) {
      const blade = this.thrown[index];
      const thrownAction = blade.update(this.knight.x, this.knight.y) | 0;
      if (blade.swishCounter === 1) {
        this.soundFX.staffSpin();
      }
      if (thrownAction === 1) {
        this.objectHolder.removeChild(blade);
        this.thrown.splice(index, 1);
        this.knight.returnWeapon();
      }
    }
  }

  private playerSpecial(action: PlayerAction): void {
    switch (action) {
      case PlayerAction.DashStarted:
        this.setSpecialDamage();
        this.specialEffect(
          EffectId.Dash,
          this.knight.x,
          this.knight.y,
          this.knight.targetAngle,
          this.knight.swordLevel,
        );
        break;
      case PlayerAction.DashEnded:
        this.killDashEffects();
        break;
      case PlayerAction.AxeShockwave:
        this.setSpecialDamage();
        this.specialEffect(
          EffectId.AxeShockwave,
          this.knight.x,
          this.knight.y,
          this.knight.shotDir,
          this.knight.swordLevel,
        );
        this.camera.shake(30);
        break;
      case PlayerAction.BlackHole:
        this.setSpecialDamage();
        this.specialEffect(
          EffectId.GravityOrb,
          this.knight.x,
          this.knight.y,
          this.knight.targetAngle,
          this.knight.swordLevel,
        );
        this.soundFX.scytheShot();
        break;
      case PlayerAction.WeaponChanged:
        this.mBoxArray.push(WEAPONS[this.knight.weapon].name);
        break;
    }
  }

  private playerSound(action: PlayerSound): void {
    switch (action) {
      case PlayerSound.Swipe:
        this.soundFX[WEAPONS[this.knight.weapon].swipeSound]();
        break;
      case PlayerSound.Dash:
        this.soundFX.dash();
        break;
      case PlayerSound.WeaponChange:
        this.soundFX.weaponChange();
        break;
      case PlayerSound.AxeCharge:
        this.soundFX.axeCharge();
        break;
      case PlayerSound.AxeShockwave:
        this.soundFX.axeShockwave();
        break;
      case PlayerSound.SwordSpin:
        this.soundFX.swordSpin();
        this.setSpecialDamage();
        break;
      case PlayerSound.StaffThrow:
        this.soundFX.staffThrow();
        this.setSpecialDamage();
        break;
      case PlayerSound.WeaponCaught:
        this.soundFX.staffCatch();
        break;
    }
  }

  private playerDeathEvents(): void {
    const knightAction = this.knight.updateDeath();
    if (knightAction.action === PlayerDeathAction.HideAndExplode) {
      this.knight.visible = false;
      this.flasherEvents(1, 1);
      this.soundFX.explodeHuge();
      this.eg.generateEffect(
        EntityId.HugeExplosion,
        this.knight.x,
        this.knight.y,
      );
      this.soundFX.death();
      if (
        this.compiledLevel.survival === null &&
        this.itemData[this.levelId].found === 1
      ) {
        this.itemData[this.levelId].found = 0;
      }
      this.soundFX.musicFadeOut(0.02);
      for (let explosionIndex = 0; explosionIndex < 8; explosionIndex++) {
        this.specialEffect(
          EffectId.ExplosionSpike,
          this.knight.x,
          this.knight.y,
          explosionIndex * 45,
        );
      }
    }
    if (knightAction.action === PlayerDeathAction.SpawnCrossStrikes) {
      for (const angle of [45, -45]) {
        const strike = new Strike(4, this.knight.x, this.knight.y, angle);
        this.objectHolder.addChild(strike);
        this.strikes.push(strike);
      }
    }
    if (knightAction.action === PlayerDeathAction.FinishDeath) {
      this.frozen = true;
      if (this.cleanserStage > 0) {
        this.soundFX.cleanserLoop(1);
      }
      this.gui.setupMissionBar(this.compiledLevel.survival === null ? 2 : 15);
    }
  }

  private setSpecialDamage(): void {
    this.specialDamage = Math.round(
      (this.knight.swordLevel * 0.5 + 1) *
        WEAPONS[this.knight.weapon].damageSpecial,
    );
  }

  // ------------------------------------------------------------- projectiles

  private shotsVsPlayer(): void {
    for (const shot of this.enemyShots()) {
      // every projectile carries a damage value; the field is optional only
      // because enemies share the same shotData shape
      const damage = shot.shotData.damage ?? 0;
      const shotAction =
        this.knight.shotCollision(
          shot.xPos,
          shot.yPos,
          shot.hitRadius,
          damage,
        ) | 0;
      if (this.blackHole.alive) {
        const dx = this.blackHole.xPos - shot.xPos;
        const dy = this.blackHole.yPos - shot.yPos;
        if (Math.sqrt(dx * dx + dy * dy) < this.blackHole.scaleX * 64) {
          shot.life = 1000;
        }
      }
      if (shotAction === 1 || shotAction === 2) {
        shot.life = 1000;
      }
    }
  }

  private killAllShots(): void {
    for (const shot of this.enemyShots()) {
      shot.life = 1000;
    }
  }

  /** Shockwave effects sweep shots out of the air. */
  private shotsVsShockwave(): void {
    for (const shot of this.enemyShots()) {
      for (const eff of this.effects) {
        if (eff.id !== EffectId.AxeShockwave) continue;
        const dx = eff.x - shot.xPos;
        const dy = eff.y - shot.yPos;
        if (Math.sqrt(dx * dx + dy * dy) < eff.scaleX * 64) {
          shot.life = 1000;
        }
      }
    }
  }
  private shotsVsThrown(thrown: Thrown): void {
    for (const shot of this.enemyShots()) {
      const dx = thrown.x - shot.xPos;
      const dy = thrown.y - shot.yPos;
      if (Math.sqrt(dx * dx + dy * dy) < thrown.swordRadius + shot.hitRadius) {
        shot.life = 1000;
      }
    }
  }

  private *enemyShots(): Generator<Entity> {
    for (const entity of this.eg.allObjects as Entity[]) {
      if (entity.projectile) yield entity;
    }
  }

  private triggerEvents(): void {
    const trigger = this.knight.triggerCycle();
    if (trigger !== null) {
      if (trigger.dialogue !== undefined) {
        this.gui.newEvent(trigger.dialogue);
      }
      if (trigger.encounter !== undefined) {
        this.activateTrigger(trigger.encounter);
      }
    }
    if (
      this.triggerCurrent === null ||
      this.triggerFinished ||
      this.og.allObjects.length - this.og.objectTotalOffset !== 0 ||
      !this.hasEnemies
    ) {
      return;
    }

    const encounter = this.currentEncounter!;
    const survival = this.compiledLevel.survival;
    let nextEncounter = encounter.activateOnClear;
    if (survival !== null) {
      const loopIndex = survival.indexByEncounter.get(encounter.id);
      if (loopIndex !== undefined) {
        nextEncounter = survival.loop[(loopIndex + 1) % survival.loop.length];
      }
    }
    if (encounter.dialogueOnClear !== undefined) {
      this.gui.newEvent(encounter.dialogueOnClear);
    }
    if (nextEncounter !== undefined) {
      this.advanceBarrierEvent(nextEncounter);
      this.activateTrigger(nextEncounter);
      return;
    }
    this.triggerFinished = true;
    if (this.hasEnemies) {
      this.setBarriers(false);
      this.soundFX.effect1();
      this.mBoxArray.push("BARRIERS DOWN");
    }
  }

  private activateTrigger(encounterId: EncounterId): void {
    const encounter = this.compiledLevel.encounterById.get(encounterId);
    if (!encounter) {
      throw new Error(
        `unknown encounter '${encounterId}' in level '${this.levelId}'`,
      );
    }
    this.triggerCurrent = encounterId;
    this.currentEncounter = encounter;
    this.triggerFinished = false;
    this.hasEnemies = encounter.spawns.length > 0;
    for (const spawn of encounter.spawns) {
      this.og.generateEnemies(spawn.xx, spawn.yy, spawn.entity, spawn.count);
    }
    if (this.hasEnemies) {
      this.soundFX.doors();
    }
    this.setBarriers(true);
    const survival = this.compiledLevel.survival;
    if (survival === null) return;
    ++this.survivalWave;
    this.gui.survivalWave = this.survivalWave;
    if (this.survivalWave > 1) {
      this.gui.setupMissionBar(17);
    }
    const loopIndex = survival.indexByEncounter.get(encounterId);
    if (loopIndex === survival.loop.length - 1) {
      this.resetShots(this.survivalWave <= survival.harderAfterWave ? 1 : 2);
    }
  }

  private setBarriers(isOn: boolean): void {
    for (const barrier of this.og.barriers as Barrier[]) {
      if (barrier.encounter === this.triggerCurrent) {
        barrier.isOn = isOn;
      }
    }
  }

  private advanceBarrierEvent(nextEncounter: EncounterId): void {
    for (const barrier of this.og.barriers as Barrier[]) {
      if (barrier.encounter === this.triggerCurrent) {
        barrier.encounter = nextEncounter;
      }
    }
  }

  private barrierObstacles(): Obstacle[] {
    const obstacles = this.barrierObstacleArray;
    let obstacleCount = 0;
    for (const barrier of this.og.barriers as Barrier[]) {
      if (!barrier.isOn) continue;
      let obstacle = this.barrierObstaclePool[obstacleCount];
      if (obstacle === undefined) {
        obstacle = { x1: 0, x2: 0, y1: 0, y2: 0 };
        this.barrierObstaclePool.push(obstacle);
      }
      if (barrier.id < 3) {
        obstacle.x1 = barrier.xx;
        obstacle.x2 = barrier.xx + 64;
        obstacle.y1 = barrier.yy + 16;
        obstacle.y2 = barrier.yy + 48;
      } else {
        obstacle.x1 = barrier.xx + 16;
        obstacle.x2 = barrier.xx + 48;
        obstacle.y1 = barrier.yy;
        obstacle.y2 = barrier.yy + 64;
      }
      obstacles[obstacleCount] = obstacle;
      obstacleCount++;
    }
    obstacles.length = obstacleCount;
    return obstacles;
  }

  // ------------------------------------------------------------- gui

  private guiEvents(): void {
    this.gui.update(this.input);
    this.gui.updateWeaponSlot(
      this.knight.energy,
      WEAPONS[this.knight.weapon].specialCost,
      this.knight.swordLevel,
      SWORD_COLOURS[this.knight.swordLevel],
    );
    if (this.gui.soundAction > 0) {
      if (this.gui.soundAction === 1) {
        this.soundFX.textBoxAppear();
      }
      this.gui.soundAction = 0;
    }
    if (this.gui.action > 0) {
      this.runGuiAction(this.gui.action);
      this.gui.action = GuiAction.None;
    }
    if (this.gui.lvlComplete) {
      this.levelCompleteEvents(this.gui.updateComplete() | 0);
    }
  }

  private runGuiAction(action: GuiAction): void {
    switch (action) {
      case GuiAction.MissionStarted: {
        this.knight.gainControl();
        const survival = this.compiledLevel.survival;
        if (survival !== null) {
          this.activateTrigger(survival.loop[0]);
        } else if (
          this.levelDocument.startDialogue !== undefined &&
          (!this.levelDocument.firstRunDialogueOnly ||
            (this.highScores[this.levelId] ?? 0) === 0)
        ) {
          this.gui.newEvent(this.levelDocument.startDialogue);
        } else if (this.levelDocument.cleanser !== undefined) {
          this.cleanser.alterState();
        }
        break;
      }
      case GuiAction.MissionBarIn:
        this.soundFX.missionBar1();
        break;
      case GuiAction.MissionBarOut:
        this.soundFX.missionBar2();
        break;
      case GuiAction.Win:
        this.win();
        break;
      case GuiAction.GargantuanExplosion:
        this.soundFX.explodeGargantuan();
        this.soundFX.musicFadeOut(0.003);
        break;
      case GuiAction.BossVanishAndAwardWeapon: {
        this.og.allObjects[0].rollData.vanished = true;
        const slot = this.bossWeaponSlot;
        if (slot !== undefined && this.weaponData[slot] === 0) {
          this.weaponData[slot] = 1;
          this.saveData();
        }
        break;
      }
      case GuiAction.BossStop:
        this.og.allObjects[0].bossEvent(0);
        break;
      case GuiAction.BossResume:
        this.og.allObjects[0].bossEvent(1);
        break;
      case GuiAction.ActivateEncounter: {
        const encounter = this.gui.encounterTarget;
        if (encounter === null) {
          throw new Error("activate-encounter dialogue has no target");
        }
        this.activateTrigger(encounter);
        this.gui.encounterTarget = null;
        break;
      }
      case GuiAction.CameraShake:
        this.camera.shake(32, true);
        break;
      case GuiAction.StartCleanser:
        this.cleanser.alterState();
        break;
      case GuiAction.FinalShardBurst:
        for (let shardIndex = 0; shardIndex < 12; shardIndex++) {
          this.specialEffect(
            EffectId.XenosShard,
            this.og.allObjects[0].xPos,
            this.og.allObjects[0].yPos,
            shardIndex * 30,
          );
        }
        break;
      case GuiAction.BossFlashAndResume:
        this.flasherEvents(1, 3);
        this.soundFX.shine();
        this.og.allObjects[0].bossEvent(1);
        this.eg.freeze = 52;
        break;
      case GuiAction.StartBossMusic:
        this.soundFX.musicStart(10);
        break;
      case GuiAction.ReturnToMainMenu:
        this.gotoScene = "main menu";
        this.startFade(true, false);
        break;
    }
  }

  private levelCompleteEvents(action: number): void {
    if (action === 1) {
      if (this.compiledLevel.survival === null) {
        this.gotoScene = "level select";
        this.startFade(true, false);
      } else {
        this.survivalDebrief();
      }
    }
    if (action === 2) {
      this.frozen = true;
    }
    if (action === 3) {
      this.bankRewards();
      this.startFade(true, false);
    }
  }

  private survivalDebrief(): void {
    const previousHighScore = this.highScores[this.levelId] ?? 0;
    if (this.score > previousHighScore) {
      this.gui.newHighScore();
      this.highScores[this.levelId] = this.score;
      this.saveData();
      this.soundFX.effect3();
    }
    const survival = this.levelDocument.survival!;
    const debrief = survival.debriefs.find(
      (entry) => entry.belowScore === null || this.score < entry.belowScore,
    );
    if (!debrief) {
      throw new Error(`survival level '${this.levelId}' has no debrief`);
    }
    this.gui.newEvent(debrief.dialogue, true, this.score);
  }

  /** Turns everything picked up this run into permanent unlocks. */
  private bankRewards(): void {
    for (
      let weaponIndex = 0;
      weaponIndex < this.weaponData.length;
      weaponIndex++
    ) {
      if (this.weaponData[weaponIndex] !== 1) continue;
      const boss = this.levelDocument.boss;
      const dialogue =
        this.bossWeaponSlot === weaponIndex
          ? boss?.weaponUnlockDialogue
          : undefined;
      if (dialogue !== undefined) {
        this.eventStack.push(dialogue);
      }
      this.weaponData[weaponIndex] = 2;
      this.saveData();
    }
    const item = this.itemData[this.levelId];
    if (item.found === 1) {
      item.found = 2;
      if (this.levelDocument.hiddenCollectible === "weapon-chip") {
        if (this.itemFound.weaponChip === 0) {
          this.eventStack.push("first-weapon-chip");
        }
        ++this.itemFound.weaponChip;
        if (this.itemFound.weaponChip === 5) {
          this.weaponData[4] = 2;
          this.eventStack.push("unlock-megatron");
        }
      } else if (this.levelDocument.hiddenCollectible === "armour-chip") {
        if (this.itemFound.armourChip === 0) {
          this.eventStack.push("first-armour-chip");
        }
        ++this.itemFound.armourChip;
        if (this.itemFound.armourChip === 5) {
          this.hyperArmour = true;
          this.eventStack.push("unlock-barrier-wing");
        }
      }
      this.saveData();
    }
    const completion = this.levelDocument.completion;
    const milestone = completion.storyMilestone;
    if (milestone !== undefined) {
      if (this.savedProgress < milestone) {
        this.gotoScene = COMPLETION_SCENES[completion.scene];
        this.savedProgress = milestone;
        this.saveData();
      } else {
        this.gotoScene = "level select";
      }
    } else {
      this.gotoScene = COMPLETION_SCENES[completion.scene];
    }
  }

  private messageBoxEvents(): void {
    if (this.mBoxArray.length > 0 && this.mBox == null) {
      this.mBox = new MessageBox(this.mBoxArray[0]);
      this.addChild(this.mBox);
      this.mBoxArray.splice(0, 1);
    }
    if (this.mBox != null && ((this.mBox.update() ?? 0) | 0) === 1) {
      this.removeChild(this.mBox);
      this.mBox = RELEASED;
    }
  }

  // ------------------------------------------------------------- enemies

  /** Melee hits landing on the player, plus black-hole capture. */
  private enemyMelee(): void {
    let blackHoleHit = 0;
    for (const enemy of this.og.allObjects as Entity[]) {
      if (enemy.dead) continue;
      if (enemy.rollData.slashState === 3) {
        if (enemy.meleeDamage > 0) {
          this.knight.damage(enemy.meleeDamage, enemy.targetAngle);
        }
        const enemyStrike = new Strike(
          5,
          this.knight.x,
          this.knight.y,
          enemy.targetAngle,
        );
        this.objectHolder.addChild(enemyStrike);
        this.strikes.push(enemyStrike);
        this.eg.freeze = 6;
        this.soundFX.enemySlash();
      }
      if (!this.blackHole.alive) continue;
      const dx = this.blackHole.xPos - enemy.xPos;
      const dy = this.blackHole.yPos - enemy.yPos;
      if (Math.sqrt(dx * dx + dy * dy) >= this.blackHole.scaleX * 64) continue;
      let bhHitFrame = ++blackHoleHit * 5;
      if (bhHitFrame >= 40) bhHitFrame -= 35;
      enemy.rollData.blackHoleFrame = bhHitFrame;
      enemy.blackHole();
    }
  }

  /** Fires every enemy whose semantic weapon channel is ready this frame. */
  private enemyFire(channelId: string): void {
    const channel = this.shotChannelsById.get(channelId);
    const pattern = this.rangedPatterns.get(channelId);
    if (!channel || !pattern) return;
    let fired = false;
    for (const enemy of this.og.allObjects as Entity[]) {
      if (
        enemy.dead ||
        enemy.projectileChannel !== channelId ||
        enemy.inBlackHole ||
        enemy.rollData.slashState !== 0 ||
        enemy.shotData.inRange !== 1 ||
        enemy.rollData.alternate !== channel.alternate
      ) {
        continue;
      }
      fired = true;
      const shotDir =
        Math.atan2(this.knight.y - enemy.yPos, this.knight.x - enemy.xPos) *
        (180 / Math.PI);
      const shotX = enemy.flipped
        ? enemy.xPos + enemy.rollData.shotOffsetX
        : enemy.xPos - enemy.rollData.shotOffsetX;
      const shotY = enemy.yPos + enemy.rollData.shotOffsetY;
      this.emit(pattern.projectiles, shotX, shotY, shotDir);
      if (
        pattern.finalRepeatProjectile !== undefined &&
        channel.repeat === channel.repeatLimit - 1
      ) {
        const shot = pattern.finalRepeatProjectile;
        this.eg.generateEffect(
          shot.effect,
          shotX,
          shotY,
          shot.absolute ? shot.angle : shotDir + shot.angle,
        );
        if (pattern.finalRepeatSound === "missileLaunch") {
          this.requestSound(Slot.Missile);
        }
      }
    }
    if (!fired || pattern.sound === undefined) return;
    const play = PROJECTILE_SOUND_PLAYERS[pattern.sound];
    if (!play) throw new Error(`unknown projectile sound '${pattern.sound}'`);
    play(this.soundFX);
  }

  private releaseBlackHole(): void {
    for (const enemy of this.og.allObjects as Entity[]) {
      if (!enemy.dead) enemy.blackHole(false);
    }
  }

  /** Anything the Cleanser wall has swept past dies instantly. */
  private cleanserKills(): void {
    const objects = this.og.allObjects as Entity[];
    for (let enemyIndex = 0; enemyIndex < objects.length; enemyIndex++) {
      const enemy = objects[enemyIndex];
      if (enemy.dead || !enemy.canBeDamagedByCleanser) continue;
      if (enemy.xPos <= this.cleanser.xPos) {
        enemy.rollData.rollLimit = 5;
        this.og.hitEnemy(enemyIndex, 1, 0, 9999);
      }
    }
  }

  private blackHoleTickDamage(): void {
    for (const enemy of this.og.allObjects as Entity[]) {
      if (enemy.dead || !enemy.inBlackHole) continue;
      if (enemy.rollData.blackHoleFrame === this.blackHoleTick) {
        enemy.blackHoleDamage(this.specialDamage);
        this.knight.updateEnergy();
      }
    }
  }

  /** Wipes the boss's escorts once the boss itself dies. */
  private killEscorts(): void {
    for (let i = 1; i < this.og.allObjects.length; i++) {
      this.og.hitEnemy(i, 1, Math.floor(Math.random() * 360), 9999);
    }
  }

  // ------------------------------------------------------------- boss

  private bossInit(): void {
    const behavior = (this.og.allObjects[0] as Entity).bossBehavior;
    if (behavior === null) {
      throw new Error(
        `boss '${this.levelDocument.boss?.entity}' has no behavior data`,
      );
    }
    const actions = new Map<number, BossActionDocument>();
    for (const [actionId, action] of Object.entries(behavior.actions)) {
      actions.set(Number(actionId), action);
    }
    const patterns: Record<string, CompiledBossPattern> = {};
    for (const [patternId, pattern] of Object.entries(behavior.patterns)) {
      validateProjectileSound(pattern.sound);
      patterns[patternId] = {
        projectiles: pattern.projectiles.map(compileProjectile),
        spin: pattern.spinDegrees,
        sound: pattern.sound,
        missileSound: pattern.missileSound,
      };
    }
    this.bossData = {
      actionCounter: 0,
      actionLimit: 0,
      actionCurrent: 0,
      shotCounter: 0,
      burstsFired: 0,
      shotPattern: null,
      shotLimit: 0,
      shotSpacing: 0,
      shooting: false,
      shotX: behavior.rig.shotX,
      shotY: behavior.rig.shotY,
      dead: false,
      explodeCounter: 0,
      shotAngle: 0,
      explodeDist: behavior.rig.explosionRadius,
      actions,
      patterns,
    };
    this.bossNextAction();
  }

  /** Steps the boss script forward one action. */
  private bossNextAction(): void {
    const boss = this.bossData;
    ++boss.actionCurrent;
    boss.shooting = false;
    boss.shotCounter = 0;
    boss.burstsFired = 0;
    boss.actionCounter = 0;
    const action = boss.actions.get(boss.actionCurrent);
    if (!action) return;
    if (action.idleFrames !== undefined) {
      boss.actionLimit = action.idleFrames;
    }
    if (action.shot) {
      boss.shooting = true;
      boss.shotPattern = action.shot.pattern;
      boss.shotSpacing = action.shot.spacingFrames;
      boss.shotLimit = action.shot.bursts;
      if (action.shot.resetAngle) boss.shotAngle = 0;
    }
    if (action.loopTo !== undefined) {
      boss.actionCurrent = action.loopTo;
    }
  }

  private bossUpdate(): void {
    const boss = this.og.allObjects[0] as Entity;
    const state = this.bossData;
    if (
      boss.rollData.hpCurrent > 0 &&
      this.gui.enemyAttacksOn &&
      this.gui.eventObject == null
    ) {
      const shotDir =
        Math.atan2(this.knight.y - boss.yPos, this.knight.x - boss.xPos) *
        (180 / Math.PI);
      if (state.shooting) {
        ++state.shotCounter;
        if (state.shotCounter === state.shotSpacing) {
          this.bossFire(boss, shotDir);
        }
      } else {
        ++state.actionCounter;
        if (state.actionCounter === state.actionLimit) {
          this.bossNextAction();
        }
      }
      if (boss.rollData.hpCurrent < 35 && state.actionCurrent < 99) {
        this.killAllShots();
        this.soundFX.enemySlash();
        state.shooting = false;
        state.shotCounter = 0;
        state.actionCounter = 0;
        state.actionCurrent = 100;
        this.bossNextAction();
        const dialogue = this.compiledLevel.bossData?.lowHealthDialogue;
        if (dialogue !== undefined) this.gui.newEvent(dialogue);
      }
    }
    if (boss.rollData.hpCurrent === 0 && !state.dead) {
      state.dead = true;
      this.scorePopUp(boss.xPos, boss.yPos, boss.rewardPoints, false);
      this.killEscorts();
      this.killAllShots();
      const dialogue = this.compiledLevel.bossData?.defeatedDialogue;
      if (dialogue !== undefined) this.gui.newEvent(dialogue);
    }
    if (state.dead && !boss.rollData.vanished) {
      this.bossDeathThroes(boss);
    }
  }

  private bossFire(boss: Entity, shotDir: number): void {
    const state = this.bossData;
    const shotX = boss.xPos + state.shotX;
    const shotY = boss.yPos + state.shotY;
    const pattern =
      state.shotPattern === null
        ? undefined
        : state.patterns[state.shotPattern];
    if (pattern) {
      const base = pattern.spin === undefined ? shotDir : state.shotAngle;
      this.emit(pattern.projectiles, shotX, shotY, shotDir, base);
      if (pattern.spin !== undefined) state.shotAngle += pattern.spin;
      if (pattern.sound) PROJECTILE_SOUND_PLAYERS[pattern.sound](this.soundFX);
      if (pattern.missileSound) this.requestSound(Slot.Missile);
    }
    ++state.burstsFired;
    if (state.burstsFired === state.shotLimit) {
      this.bossNextAction();
    } else {
      state.shotCounter = 0;
    }
  }

  private bossDeathThroes(boss: Entity): void {
    const state = this.bossData;
    ++state.explodeCounter;
    if (state.explodeCounter % 3 === 0 && state.explodeCounter <= 12) {
      const rad = Math.floor(Math.random() * 360) * (Math.PI / 180);
      const dist = Math.floor(Math.random() * state.explodeDist);
      const explodeX = dist * Math.cos(rad) + boss.xPos;
      const explodeY = dist * Math.sin(rad) + boss.yPos;
      if (state.explodeCounter < 10) {
        this.eg.generateEffect(EntityId.Explosion, explodeX, explodeY);
      } else {
        this.eg.generateEffect(EntityId.LargeExplosion, explodeX, explodeY);
        this.camera.shake(16);
      }
    }
    if (state.explodeCounter === 12) {
      this.requestSound(Slot.ExplodeEnemy);
      state.explodeCounter = 2;
    }
  }

  /** Spawns one shot pattern; `base` is what non-absolute angles are relative to. */
  private emit(
    pattern: readonly Projectile[],
    x: number,
    y: number,
    aim: number,
    base = aim,
  ): void {
    for (const shot of pattern) {
      this.eg.generateEffect(
        shot.effect,
        x,
        y,
        shot.absolute ? shot.angle : base + shot.angle,
      );
    }
  }

  // ------------------------------------------------------------- hazards

  private cleanserSetup(): void {
    if (this.levelDocument.cleanser === undefined) return;
    this.cleanserStage = 1;
    this.cleanser = new Cleanser(this.view.levelY);
    this.objectHolder.addChild(this.cleanser);
    this.soundFX.cleanserLoop(0);
  }

  private cleanserEvents(): void {
    const closeness = this.cleanser.update(this.knight.x) | 0;
    if (this.knight.x <= this.cleanser.xPos) {
      this.knight.x = this.cleanser.xPos;
      this.knight.damage(1000, 0);
    }
    this.cleanserKills();
    this.camera.shake((closeness * 0.05) | 0);
    const volume = Math.min(100, Math.max(35, (closeness * 0.5) | 0));
    this.soundFX.cleanserVolume(volume * 0.01);
  }

  private blackHoleEvents(): void {
    if (!this.blackHole.isOn) return;
    const bhAction =
      this.blackHole.update(this.objectHolder.x, this.objectHolder.y) | 0;
    if (bhAction === 1) {
      this.releaseBlackHole();
    }
    if (bhAction === 2) {
      this.knight.blackHoleOn = false;
    }
    if (!this.blackHole.alive) return;
    ++this.blackHoleTick;
    if (this.blackHoleTick % 5 === 0 && this.blackHoleTick <= 35) {
      this.blackHoleTickDamage();
    }
    if (this.blackHoleTick === 35) {
      this.blackHoleTick = 0;
    }
  }

  /** Damages everything inside a special attack's blast radius. */
  private specialCollision(
    x: number,
    y: number,
    angle: number,
    scale: number,
  ): void {
    let totalHit = 0;
    for (
      let enemyIndex = 0;
      enemyIndex < this.og.allObjects.length;
      enemyIndex++
    ) {
      if (totalHit >= this.knight.swordLevel + 3) continue;
      const enemy = this.og.allObjects[enemyIndex] as Entity;
      if (enemy.dead) continue;
      const dx = x - enemy.xPos;
      const dy = y - enemy.yPos;
      if (Math.sqrt(dx * dx + dy * dy) < scale * 64) {
        totalHit++;
        this.og.hitEnemy(enemyIndex, totalHit, angle, this.specialDamage);
        this.knight.updateEnergy();
      }
    }
  }

  // ------------------------------------------------------------- effects

  private strikeCycle(): void {
    for (
      let strikeIndex = 0;
      strikeIndex < this.strikes.length;
      strikeIndex++
    ) {
      if (this.strikes[strikeIndex].update()) {
        this.objectHolder.removeChild(this.strikes[strikeIndex]);
        this.strikes.splice(strikeIndex, 1);
        strikeIndex--;
      }
    }
  }

  private scorePopUp(
    x: number,
    y: number,
    amount: number,
    multiplied: boolean,
  ): void {
    if (multiplied) {
      amount *= this.knight.swordLevel + 1;
    }
    const scorePop = new ScorePop(
      x,
      y,
      amount,
      multiplied,
      this.knight.swordLevel,
    );
    this.objectHolder.addChild(scorePop);
    this.scoreArray.push(scorePop);
    this.score += amount;
    this.gui.updateScore(this.score);
  }

  private scoreEvents(): void {
    for (
      let scoreIndex = 0;
      scoreIndex < this.scoreArray.length;
      scoreIndex++
    ) {
      if (this.scoreArray[scoreIndex].update()) {
        this.objectHolder.removeChild(this.scoreArray[scoreIndex]);
        this.scoreArray.splice(scoreIndex, 1);
        scoreIndex--;
      }
    }
  }

  private effectCycle(): void {
    for (
      let effectIndex = 0;
      effectIndex < this.effects.length;
      effectIndex++
    ) {
      const effect = this.effects[effectIndex];
      switch (effect.id) {
        case EffectId.Dash:
          effect.updateDash(this.knight.x, this.knight.y);
          break;
        case EffectId.SwordTrail:
          effect.updateTrail(this.knight.x, this.knight.y);
          break;
        case EffectId.AxeShockwave: {
          const shockwave = effect.updateMove();
          if (effect.hitCD === 0) {
            if (!shockwave.fading) {
              this.specialCollision(
                shockwave.xx | 0,
                shockwave.yy | 0,
                shockwave.angle,
                shockwave.scale,
              );
            }
            this.shotsVsShockwave();
            effect.hitCD = 6;
          }
          break;
        }
        case EffectId.GravityOrb: {
          if (effect.updateGravityBall()) {
            this.blackHole.appear(effect.x, effect.y, this.knight.swordLevel);
            this.blackHoleTick = 0;
            this.soundFX.scytheBlackHole();
            this.camera.shake(40);
          }
          break;
        }
        case EffectId.ExplosionSpike:
          effect.updateMove();
          break;
        default:
          if (
            effect.id >= EffectId.SmallHealthPickup &&
            effect.id < EffectId.GravityOrb &&
            !effect.fading
          ) {
            this.pickupEvents(effect);
          }
          break;
      }
      if (effect.id === EffectId.XenosShard) {
        effect.updateXenosShard();
      }
      if ((effect.updateAlpha() | 0) === 1) {
        this.objectHolder.removeChild(effect);
        this.effects.splice(effectIndex, 1);
        effectIndex--;
      }
    }
  }

  private pickupEvents(eff: Effect): void {
    const itemAction = eff.updateItem(this.knight.x, this.knight.y) | 0;
    switch (itemAction) {
      case 1:
        this.collectEnergy(
          eff,
          5,
          250,
          () => this.soundFX.item1(),
          EffectId.SmallPickupBurst,
          10,
        );
        break;
      case 2:
        this.collectEnergy(
          eff,
          10,
          500,
          () => this.soundFX.item2(),
          EffectId.MediumPickupBurst,
          25,
        );
        break;
      case 3:
        this.collectEnergy(
          eff,
          25,
          1000,
          () => this.soundFX.item3(),
          EffectId.LargePickupBurst,
          50,
        );
        break;
      case 4:
      case 5:
        this.soundFX.item3();
        this.specialEffect(EffectId.LargePickupBurst, eff.x, eff.y);
        this.gui.setupMissionBar(4);
        this.itemData[this.levelId].found = 1;
        this.scorePopUp(eff.x, eff.y, 500, false);
        break;
    }
  }

  private collectEnergy(
    eff: Effect,
    hp: number,
    xenos: number,
    sound: () => void,
    sparkle: EffectId,
    points: number,
  ): void {
    this.knight.gainHP(hp);
    this.knight.updateEnergy(xenos);
    sound();
    this.specialEffect(sparkle, eff.x, eff.y);
    this.scorePopUp(eff.x, eff.y, points, true);
  }

  private killDashEffects(): void {
    for (const eff of this.effects) {
      if (eff.id === EffectId.Dash) eff.kill();
    }
  }

  private specialEffect(
    id: EffectId = EffectId.Dash,
    x = 0,
    y = 0,
    extraValue = 0,
    swordLevel = 0,
  ): void {
    const effect = new Effect(id, x, y, extraValue, swordLevel);
    this.objectHolder.addChild(effect);
    this.effects.push(effect);
  }

  private flasherEvents(mode = 1, flashTotal = 1): void {
    if (mode === 0) {
      this.flasher = new MovieClip();
      this.addChild(this.flasher);
      const whiteFill = new Sprite();
      whiteFill.graphics.beginBitmapFill(new whiteBlock());
      whiteFill.graphics.drawRect(0, 0, 720, 480);
      whiteFill.graphics.endFill();
      this.flasher.addChild(whiteFill);
      this.flasher.visible = false;
    }
    if (mode === 1) {
      this.flashTotal = flashTotal - 1;
      this.flashAlpha = 75;
      this.flasher.visible = true;
    }
    if (mode === 2 && this.flashAlpha > 0) {
      this.flashAlpha -= 5;
      if (this.flashAlpha === 20 && this.flashTotal > 0) {
        --this.flashTotal;
        this.flashAlpha = 65;
      }
      this.flasher.alpha = this.flashAlpha * 0.01;
      if (this.flashAlpha === 0) {
        this.flasher.visible = false;
      }
    }
  }

  // ------------------------------------------------------------- input & camera

  private scrolling(): void {
    if (
      this.gui.speakerTarget.length > 0 &&
      this.compiledLevel.bossData !== null
    ) {
      this.camera.target =
        this.gui.speakerTarget[0] < 2 ? CameraTarget.Player : CameraTarget.Boss;
      this.gui.speakerTarget.splice(0, 1);
    }
    this.camera.update(
      this.objectHolder,
      this.knight,
      this.og.allObjects[0] as Entity | undefined,
      this.view,
    );
  }

  // ------------------------------------------------------------- lifecycle

  private resetShots(waveIndex = 0): void {
    const waves =
      this.levelDocument.survival?.shotWaves ??
      this.content.survival.survival!.shotWaves;
    const wave = waves[waveIndex];
    this.shotChannels = wave.channels.map((channel) => ({
      id: channel.id,
      counter: channel.counter,
      limit: channel.limit,
      repeat: 0,
      repeatLimit: channel.repeatLimit,
      repeatSpacing: channel.repeatSpacing ?? 0,
      alternate: false,
    }));
    this.shotChannelsById.clear();
    for (const channel of this.shotChannels) {
      this.shotChannelsById.set(channel.id, channel);
    }
  }
}

function compileRangedPatterns(
  content: ContentRepository,
  level: LevelDocument,
): ReadonlyMap<string, CompiledRangedPattern> {
  const result = new Map<string, CompiledRangedPattern>();
  for (const encounter of level.encounters) {
    for (const spawn of encounter.spawns ?? []) {
      const definition = content.entity(spawn.entity);
      const behavior = definition.behaviorData;
      if (
        behavior === undefined ||
        !("projectilePattern" in behavior) ||
        result.has(behavior.channel)
      ) {
        continue;
      }
      const pattern = behavior.projectilePattern;
      validateProjectileSound(pattern.sound);
      validateProjectileSound(pattern.finalRepeatSound);
      result.set(behavior.channel, {
        projectiles: pattern.projectiles.map(compileProjectile),
        sound: pattern.sound,
        finalRepeatProjectile:
          pattern.finalRepeatProjectile === undefined
            ? undefined
            : compileProjectile(pattern.finalRepeatProjectile),
        finalRepeatSound: pattern.finalRepeatSound,
      });
    }
  }
  return result;
}

function compileProjectile(document: ProjectileDocument): Projectile {
  const effect = PROJECTILE_EFFECTS[document.effect];
  if (effect === undefined) {
    throw new Error(`unknown projectile effect '${document.effect}'`);
  }
  return {
    effect,
    angle: document.angle,
    absolute: document.absolute,
  };
}

function validateProjectileSound(sound: string | undefined): void {
  if (sound !== undefined && PROJECTILE_SOUND_PLAYERS[sound] === undefined) {
    throw new Error(`unknown projectile sound '${sound}'`);
  }
}
